import React from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';

import Button from 'relements/build/esm/ui/button';
import Icon from 'relements/build/esm/ui/icon';
import Accordion from 'relements/build/esm/ui/accordion';
import ContextMenu from 'relements/build/esm/overlays/contextMenu';
import Loader from 'relements/build/esm/decorators/loader';

import connection from '@src/connection';
import hashHistory from '@src/History';

import { GOOGLE_ANALYTICS, PERMISSION_LEVELS } from '@src/const';
import {
  AlertTypes,
  CONTEXT_CLEAR_NODE,
  END_OF_QUERY,
} from '@src/constants/MogamboConstants';
import { LANGUAGE_CODE_TO_TITLE } from '@src/constants/Language';
import AddLanguage from '@src/components/BotModals/AddLanguage';
import BotStore from '@src/store';

import GeneralNodeItem from './components/GeneralNodeItem';
import * as maps from './maps';
import './GeneralNodes.scss';

@Loader({})
@connection(maps.listenToStores, maps.getStateFromStores)
class GeneralNodes extends React.Component {
  constructor() {
    super();

    this.state = {
      edited: false,
      nodeData: [],
      editedNodeData: {},
    };
  }

  componentWillReceiveProps(nextProps) {
    if (
      (!this.props.loaderActive &&
        this.props.currentLanguage.languageSource !==
          nextProps.currentLanguage.languageSource) ||
      this.props.currentLanguage.languageScript !==
        nextProps.currentLanguage.languageScript
    ) {
      this.props.activateLoader();
      maps.getGeneralNodes(
        this.props.match.params.botId,
        this.props.match.params.storyId,
        nextProps.currentLanguage,
        () => this.props.deactivateLoader()
      );
      return;
    }

    if (
      isEqual(this.props.generalNodes, nextProps.generalNodes) &&
      isEqual(this.props.editedGeneralNodes, nextProps.editedGeneralNodes)
    ) {
      return;
    }

    const nodeData = {};
    const editedNodeData = {};

    nextProps.generalNodes.forEach((node) => {
      nodeData[node.node_id] = node;
    });

    nextProps.editedGeneralNodes.forEach((node) => {
      nodeData[node.node_id] = node;
      editedNodeData[node.node_id] = node;
    });

    this.setState({
      edited: false,
      nodeData,
      editedNodeData,
    });
  }

  componentDidMount() {
    if (!this.props.loaderActive) {
      this.props.activateLoader();
      maps.getGeneralNodes(
        this.props.match.params.botId,
        this.props.match.params.storyId,
        this.props.currentLanguage,
        () => {
          this.props.deactivateLoader();
        }
      );
    }
  }

  componentWillUnmount() {
    maps.clearGeneralNodes();
  }

  render() {
    return (
      <div
        className={`general-nodes-overlay ${
          BotStore.getSelfServeLayout() ? 'general-nodes-overlay-ww' : ''
        }`}
      >
        {this.renderHeader()}
        {this.renderBody()}
      </div>
    );
  }

  // renders header
  renderHeader = () => {
    return (
      <div className="general-nodes-overlay-header">
        {this.props.match.params.storyId ? (
          <div className="general-nodes-overlay-header-title">
            Story General Nodes
          </div>
        ) : null}
        <div className="general-nodes-overlay-header-actions">
          {this.renderLanguageDropdown()}
          <Button id="add-new-node" onClick={this.onAddNewNode}>
            Add Node
          </Button>
          <Button id="upload-qna-button" onClick={this.onUploadQA}>
            Upload Q&A
          </Button>
        </div>
        <Button
          type={Button.TYPES.PRIMARY}
          onClick={this.onSave}
          disabled={!this.state.edited}
        >
          Save
        </Button>
        <Icon
          src="close"
          className="general-nodes-overlay-header-icon"
          onClick={this.onClose}
        />
      </div>
    );
  };

  // renders lanuage dropdown in header
  // disabling adding new language temporarily for ML
  renderLanguageDropdown = () => {
    const { languageSource = 'en' } = this.props.currentLanguage;
    return (
      <ContextMenu.Button
        ref={(ref) => {
          if (ref) this._languageMenuRef = ref;
        }}
        size="big"
        title={(
          <div className="center-align-flex">
            &nbsp;
            {languageSource.toUpperCase()}
          </div>
        )}
        offset={{ top: 44, left: -72 }}
      >
        {this.props.supportedLanguages.map((languageCode) => {
          return (
            <ContextMenu.Item
              size="big"
              key={languageCode}
              onClick={(e) => {
                this.updateCurrentLanguage(e, languageCode);
              }}
            >
              {languageCode.toUpperCase()}
              {' '}
              -
              {' '}
              {LANGUAGE_CODE_TO_TITLE[languageCode]}
            </ContextMenu.Item>
          );
        })}
        <ContextMenu.Item
          size="big"
          disabled
          // onClick={e => {
          //   this.showAddLanguageModal(e);
          // }}
        >
          + Add Language
        </ContextMenu.Item>
      </ContextMenu.Button>
    );
  };

  // renders body of the page
  renderBody = () => {
    const orderedNodes = Object.values(this.state.nodeData).sort(
      (node1, node2) => {
        // Special handling for new node
        if (node1.new_node) return -1;
        if (node2.new_node) return 1;
        return node1.system_name > node2.system_name ? 1 : -1;
      }
    );

    return (
      <div className="general-nodes-overlay-body">
        <div className="general-nodes-overlay-body-header">
          <div className="general-nodes-overlay-body-text">
            <div className="general-nodes-overlay-body-title">
              FAQs for Bots
            </div>
            <div className="general-nodes-overlay-body-subtitle">
              Define user messages & bot responses here that are applicable bot
              wide.
            </div>
          </div>
        </div>
        {orderedNodes.length === 0 ? (
          <span style={{ opacity: 0.6, fontSize: 12 }}>No FAQ Steps</span>
        ) : (
          <Accordion className="general-nodes-overlay-body-card">
            {orderedNodes.map(this.renderGeneralNode())}
          </Accordion>
        )}
        <input
          type="file"
          id="upload-qna"
          name="upload-qna"
          style={{
            visibility: 'hidden',
            width: '1px',
            height: '1px',
          }}
        />
      </div>
    );
  };

  // renders general node
  renderGeneralNode = () => (node, i) => {
    const isActive = this.state.activeIndex === i;
    const activeClassName = isActive ? 'active' : '';
    const inactiveClassName = !node.active ? 'inactive' : '';
    return (
      <Accordion.Item
        className={`general-nodes-overlay-item ${activeClassName} ${inactiveClassName}`}
        itemIndex={i}
        active={isActive}
        renderHeader={this.renderGeneralNodeHeader(node)}
        onChange={this.handleToggle}
      >
        <GeneralNodeItem
          key={`general-node-${node.node_id}`}
          {...node}
          domain={this.props.meta.domain}
          story={this.props.meta.story}
          synonyms={this.props.synonyms}
          meanings={this.props.meanings}
          updatedSynonyms={this.props.updatedSynonyms}
          dataUpdateCallback={this.dataUpdateCallback}
          id={`general-node-${node.node_id}`}
        />
      </Accordion.Item>
    );
  };

  // renders headers of general node
  renderGeneralNodeHeader = (node) => ({ ExpandIcon }) => {
    return (
      <div className="general-nodes-overlay-item-header">
        {node.title && node.system_name ? (
          <div className="general-nodes-overlay-item-header-title">
            {node.title}
            {' '}
            (
            {node.system_name}
            )
          </div>
        ) : (
          <div className="general-nodes-overlay-item-header-title zero-state">
            Enter Step Name
          </div>
        )}
        <Button onClick={this.toggleActive(node)}>
          {node.active ? 'Deactivate' : 'Activate'}
        </Button>
        <ExpandIcon />
      </div>
    );
  };

  // adds new node
  onAddNewNode = () => {
    const { nodeData, editedNodeData } = this.state;

    const randomId = Math.floor(Math.random() * 10000);
    const newObjectData = {
      node_id: randomId,
      system_name: '',
      bot_responses: [],
      active: true,
      user_responses: [],
      new_node: true,
      [CONTEXT_CLEAR_NODE.KEY]: false,
      [END_OF_QUERY]: false,
    };

    nodeData[randomId] = newObjectData;
    editedNodeData[randomId] = newObjectData;

    this.setState({
      edited: true,
      nodeData,
      editedNodeData,
      activeIndex: 0,
    });
  };

  // uploads QA from file
  onUploadQA = () => {
    document.getElementById('upload-qna').addEventListener('change', () => {
      // Check for the various File API support.
      if (window.File && window.FileReader && window.FileList && window.Blob) {
        // Great success! All the File APIs are supported.
        const reader = new FileReader();
        const fileDetails = document.getElementById('upload-qna').files[0];

        reader.onload = () => {
          this.createNodesFromFile(fileDetails.name, reader.result);
        };

        reader.readAsText(fileDetails);
      } else {
        maps.createAlert(
          'The File APIs are not fully supported in this browser.'
        );
      }

      document.getElementById('upload-qna').removeEventListener('change');
    });

    document.getElementById('upload-qna').click();

    const gaLabel = this.props.match.params.storyId
      ? GOOGLE_ANALYTICS.LABEL.STORY_QA
      : GOOGLE_ANALYTICS.LABEL.BOT_QA;
    const gaEvent = this.props.match.params.storyId
      ? GOOGLE_ANALYTICS.EVENTS.GENERAL_NODE_STORY
      : GOOGLE_ANALYTICS.EVENTS.GENERAL_NODE_BOT;
    maps.sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.GENERAL_NODES,
      gaEvent,
      gaLabel,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  };

  // creates nodes from the uploaded file
  createNodesFromFile = (filename, data) => {
    const { nodeData, editedNodeData } = this.state;

    const qnaList = this.extractQNAFromTSVFile(data);
    if (!qnaList) {
      return;
    }

    const nodePrefix = filename.split('.')[0].toUpperCase().replace(' ', '_');

    const randomId = Math.floor(Math.random() * 10000);

    qnaList.forEach((qna, index) => {
      const question = qna[0];
      const answer = qna[1];
      const node = {
        node_id: randomId + index,
        system_name: `${nodePrefix}_${index + 1}`,
        bot_responses: [answer],
        active: true,
        user_responses: [
          {
            keywords: [],
            active: true,
            id: `new-sentence-${Math.random()}`,
            sentence: question,
          },
        ],
        new_node: true,
      };

      if (!this.props.nodeContextMenuEnabled) {
        node[CONTEXT_CLEAR_NODE.KEY] = true;
      } else {
        node[END_OF_QUERY.KEY] = true;
      }

      nodeData[node.node_id] = node;
      editedNodeData[node.node_id] = node;
    });

    this.setState({
      edited: true,
      nodeData,
      editedNodeData,
    });
  };

  extractQNAFromTSVFile = (data) => {
    const rowList = data.split('\n').map((data) => data.split('\t'));

    const rows = rowList.slice(1);
    const mappedRows = rows.map((row) => {
      if (row.length < 2) {
        return [];
      }
      return [row[0], row[1]];
    });

    const filteredRows = mappedRows.filter((row) => {
      return row.length === 2;
    });

    if (!filteredRows || filteredRows.length === 0) {
      maps.createAlert('Invalid/Empty TSV file provided. Please try again.');
      return null;
    }

    return filteredRows;
  };

  dataUpdateCallback = (nodeId, fieldName, data) => {
    switch (fieldName) {
      case 'bot_responses':
        if (
          !maps.userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT) &&
          !maps.userHasPermission(PERMISSION_LEVELS.MOGAMBO_BOT_SAYS_EDIT)
        ) {
          maps.permissionErrorAlert();
          return;
        }
        break;

      default:
        if (!maps.userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
          maps.permissionErrorAlert();
          return;
        }
        break;
    }

    // Todo: Maintain a copy of the edited node data
    const { nodeData, editedNodeData } = this.state;

    nodeData[nodeId][fieldName] = data;

    if (editedNodeData[nodeId] === undefined) {
      editedNodeData[nodeId] = {
        node_id: nodeData[nodeId].node_id,
        active: nodeData[nodeId].active,
        system_name: nodeData[nodeId].system_name,
        title: nodeData[nodeId].title,
        bot_responses: nodeData[nodeId].bot_responses,
        node_entities: nodeData[nodeId].node_entities || {},
        user_responses: nodeData[nodeId].user_responses || [],
        quick_replies: nodeData[nodeId].quick_replies || [],
      };

      if (!this.props.nodeContextMenuEnabled) {
        editedNodeData[nodeId][CONTEXT_CLEAR_NODE.KEY] =
          nodeData[nodeId][CONTEXT_CLEAR_NODE.KEY];
      } else {
        editedNodeData[nodeId][END_OF_QUERY.KEY] =
          nodeData[nodeId][END_OF_QUERY.KEY];
      }
    }
    editedNodeData[nodeId][fieldName] = data;

    this.setState({
      edited: true,
      nodeData,
      editedNodeData,
    });
  };

  // On close function
  onClose = () => {
    const editedNodeList = Object.values(this.state.editedNodeData);
    if (editedNodeList.length > 0 && this.state.edited) {
      maps.showConfirmationDialog({
        okTitle: 'Discard Changes',
        cancelTitle: 'Cancel',
        title: 'Do you want to discard the changes made?',
        message: 'Changes will be lost',
        onOk: () => {
          this.closeOverlay();
        },
        onCancel: () => {},
      });
    } else {
      this.closeOverlay();
    }
  };

  // closes the overlay
  closeOverlay = () => {
    const { prevLocation = '', currentLocation = '' } = this.props.location;
    let closeLink = prevLocation;
    if (!closeLink || prevLocation === currentLocation) {
      if (this.props.match.params.storyId) {
        closeLink = `/v2/bots/${this.props.match.params.botId}/stories/${this.props.match.params.storyId}/substories`;
      } else {
        closeLink = `/bot/${this.props.match.params.botId}`;
      }
    }
    hashHistory.push(closeLink);
  };

  // saves
  onSave = () => {
    const editedNodeList = Object.values(this.state.editedNodeData);
    if (editedNodeList.length === 0) {
      maps.createAlert('Nothing has changed');
      return;
    }

    const nodeNameMap = {};
    let duplicateName = null;

    Object.values(this.state.nodeData).forEach((node) => {
      if (nodeNameMap[node.system_name]) {
        duplicateName = node.system_name;
        console.warn(`Duplicate FAQ Step Name: ${duplicateName}`);
      } else {
        nodeNameMap[node.system_name] = true;
      }
    });

    if (duplicateName) {
      maps.createAlert(
        `System Names should be unique. ${duplicateName} is duplicate.`,
        undefined,
        AlertTypes.NEGATIVE
      );
      return;
    }

    editedNodeList.forEach((node) => {
      if (!node.title) {
        maps.createAlert(
          'Step name cannot be empty.',
          undefined,
          AlertTypes.NEGATIVE
        );
        return;
      }

      if (node.title.replace(/[^a-zA-Z0-9_]/g, '').length < 3) {
        maps.createAlert(
          'Please enter a valid step name of minimum 3 letters',
          undefined,
          AlertTypes.NEGATIVE
        );
        return;
      }

      if (node.new_node && !node.system_name) {
        maps.createAlert(
          'System name cannot be empty.',
          undefined,
          AlertTypes.NEGATIVE
        );
        return;
      }

      if (node.new_node && !/^[a-zA-Z0-9_:]+$/.test(node.system_name)) {
        maps.createAlert(
          `${node.system_name} is an invalid Step Name. It can only contain A-Z, 0-9 and _`,
          undefined,
          AlertTypes.NEGATIVE
        );
        return;
      }

      if (node.user_responses) {
        // Clean the user responses
        node.user_responses = node.user_responses.filter((response) => {
          // Select only old sentences or new sentences if they have some content
          return (
            !response.id.startsWith('new-sentence') ||
            response.sentence.length > 0
          );
        });

        if (node.user_responses.length <= 0) {
          maps.createAlert(
            `Please add atleast 1 User Message for ${node.system_name}`,
            undefined,
            AlertTypes.NEGATIVE
          );
          return;
        }

        node.user_responses.forEach((response) => {
          if (!response.sentence) {
            maps.createAlert(
              `User Message cannot be empty for ${node.system_name}`,
              undefined,
              AlertTypes.NEGATIVE
            );
          }
        });
      }

      if (node.bot_responses && node.bot_responses.length <= 0) {
        maps.createAlert(
          `Please add atleast 1 Bot Responses for ${node.system_name}`,
          undefined,
          AlertTypes.NEGATIVE
        );
        return;
      }

      node.bot_responses.forEach((response) => {
        if (!response) {
          maps.createAlert(
            `Bot Responses cannot be empty for ${node.system_name}`,
            undefined,
            AlertTypes.NEGATIVE
          );
        }
      });
    });

    let gaEventAction = GOOGLE_ANALYTICS.EVENTS.GENERAL_NODE_BOT;
    let gaventLabel = GOOGLE_ANALYTICS.LABEL.BOT_ADD;

    if (this.props.match.params.storyId) {
      gaEventAction = GOOGLE_ANALYTICS.EVENTS.GENERAL_NODE_STORY;
      gaventLabel = GOOGLE_ANALYTICS.LABEL.STORY_ADD;
    }
    maps.sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.GENERAL_NODES,
      gaEventAction,
      gaventLabel,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );

    this.props.activateLoader();
    maps.saveGeneralNodes(
      {
        pending: editedNodeList,
        success: [],
        failure: [],
      },
      this.props.match.params.botId,
      this.props.match.params.storyId,
      this.props.currentLanguage,
      (success) => {
        this.props.deactivateLoader();

        if (success) {
          this.setState({
            edited: false,
          });

          maps.createAlert(
            `Changes saved successfully`,
            undefined,
            AlertTypes.POSITIVE
          );
        }
      },
      this.props.meta.domain.title
    );
  };

  // function to activate/deactivate node
  toggleActive = (node) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.dataUpdateCallback(node.node_id, 'active', !node.active);
  };

  // toggles between different accordian
  handleToggle = (newIndex) => {
    this.setState((state) => ({
      activeIndex: state.activeIndex === newIndex ? -1 : newIndex,
    }));
  };

  // Show add language modal
  showAddLanguageModal = (e) => {
    this._languageMenuRef.handleClose(e);
    maps.openComponentModal(<AddLanguage />, 'Add Language', null, 'CROSS');
  };

  // Updates current language
  updateCurrentLanguage = (e, selectedLanguage) => {
    this._languageMenuRef.handleClose(e);

    const editedNodeList = Object.values(this.state.editedNodeData);
    if (editedNodeList.length > 0 && this.state.edited) {
      maps.showConfirmationDialog({
        okTitle: 'Discard Changes',
        cancelTitle: 'Cancel',
        title: 'Do you want to discard the changes made?',
        message: 'Changes will be lost',
        onOk: () => {
          maps.updateCurrentLanguage(selectedLanguage, selectedLanguage);
        },
        onCancel: () => {},
      });
    } else {
      // Language Source is our primary identifier for all language changes
      // Hence we use the same as our current source and script when editing on Mogambo
      maps.updateCurrentLanguage(selectedLanguage, selectedLanguage);
    }
  };
}

GeneralNodes.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      storyId: PropTypes.string,
      botId: PropTypes.string,
    }),
  }),
  meta: PropTypes.shape({
    domain: PropTypes.string,
    story: PropTypes.string,
  }),
  location: PropTypes.shape({
    prevLocation: PropTypes.string,
    currentLocation: PropTypes.string,
  }),
  meanings: PropTypes.shape({}),
  synonyms: PropTypes.shape({}),
  generalNodes: PropTypes.shape(),
  editedGeneralNodes: PropTypes.arrayOf(),
  updatedSynonyms: PropTypes.arrayOf(),
  currentLanguage: PropTypes.shape({
    languageSource: PropTypes.string,
    languageScript: PropTypes.string,
  }),
  supportedLanguages: PropTypes.arrayOf(PropTypes.string),
  loaderActive: PropTypes.bool,
  activateLoader: PropTypes.func,
  deactivateLoader: PropTypes.func,
};

GeneralNodes.defaultProps = {
  meanings: {},
  match: {},
  meta: {},
  location: {},
  synonyms: {},
  generalNodes: [],
  editedGeneralNodes: [],
  updatedSynonyms: {},
  currentLanguage: {},
  supportedLanguages: [],
  loaderActive: false,
  activateLoader: () => {},
  deactivateLoader: () => {},
};

export default GeneralNodes;
